import { Component, OnInit, inject } from '@angular/core';
import { ClaimRow } from '../../../../models/api/view/claim-view/claim-row';
import { BaseComponent } from '../../../base/base.component';
import { PageHeaderComponent } from '../../../core/labels/page-header.component';
import { ExpandPanelComponent } from '../../../core/panels/expand-panel.component';
import { ColumnComponent } from '../../../core/table/column.component';
import { TableComponent } from '../../../core/table/table.component';
import { ClaimKey } from '../../../../models/api/core/claim-key';
import {
  JobFunction,
  JobTaskRef,
} from '../../../../models/api/view/claim/details/accommodations/job-function/job-function';
import { JobFunctionList } from '../../../../models/api/view/claim/details/accommodations/job-function/job-function-list';
import { getExpandSubject } from '../../../../functions/get-expand-subject';
import { samlPost } from '../../../../functions/saml-post';
import { SirSSO } from '../../../../models/api/view/claim/documentation/documents/sir-sso';
import { Snackbar } from '../../../../services/snackbar/snackbar.service';
import { LinkLabelComponent } from '../../../core/labels/link-label.component';
import { HttpErrorResponse } from '@angular/common/http';

@Component({
  selector: 'app-job-functions',
  imports: [
    PageHeaderComponent,
    TableComponent,
    ColumnComponent,
    ExpandPanelComponent,
    LinkLabelComponent,
  ],
  template: `
    <app-page-header label="Job Functions" />
    <div class="tw-w-full tw-mt-2 tw-mb-5 tw-italic">
      If you are an authorized decision maker, job functions and claims that are
      pending business review can be approved/rejected in mySedgwick.
    </div>
    <app-table
      [dataSource]="jobFunctions"
      [pageSize]="5"
      [scrollable]="false"
      recordLabel="Job Function"
      [showFilter]="false"
      [showFilterButton]="false"
      [showRecordLabel]="false"
      dataKey="jobTitle"
      [expandable]="true"
      emptyMessage="Job Function information will display here"
      #jobFunctionsTable
    >
      <app-column name="jobTitle" label="Job Title" />

      <app-column name="createdDate" label="Start Date" format="d" />
      <app-column name="endDate" label="End Date" format="d" />
      <app-column label="SIR LINK" [sort]="false">
        <ng-template let-data>
          @if (view.authorize('Images') && data.dcn) {
            <app-link-label
              [ariaLabel]="'Open SIR document, document control number ' + data.dcn"
              (click)="openSir(data.dcn)"
              >{{ data.dcn }}</app-link-label
            >
          } @else {
            <span>{{ data.dcn }}</span>
          }
        </ng-template>
      </app-column>
      <app-column [fullRow]="true">
        <ng-template let-data>
          <div class=" tw-pb-8">
            <app-expand-panel
              collapseHeight="68px"
              [expanded]="jobFunctionsTable.expanded"
              [label]="getExpandSubject(data.jobTitle) || 'Job function'"
            >
              <div class="tw-flex tw-gap-8">
                <div class="tw-basis-1/2">
                  <div class="tw-font-bold tw-pb-2">Description</div>
                  <div class="tw-text-wrap tw-leading-6">
                    {{ data.jobDescription }}
                  </div>
                </div>
                <div class="tw-basis-1/2">
                  <div class="tw-font-bold tw-pb-2">Job functions</div>
                  <div class="tw-text-wrap">
                    <ul
                      class="tw-w-full tw-list-disc tw-list-inside tw-leading-6"
                    >
                      @for (
                        function of getJobTaskRefs(data.jobFunctionId);
                        track function
                      ) {
                        <li>{{ function.jobTask }}</li>
                      }
                    </ul>
                  </div>
                </div>
              </div>
            </app-expand-panel>
          </div>
        </ng-template>
      </app-column>
    </app-table>
  `,
  styles: ``,
})
export class JobFunctionsComponent extends BaseComponent implements OnInit {
  private snackbar = inject(Snackbar);

  protected getExpandSubject = getExpandSubject;

  private static readonly loadFailedMsg =
    'Unable to load job functions. Please try again.';
  private static readonly sirSsoIncompleteMsg =
    'Unable to open the SIR document. Single sign-on could not be completed.';
  private static readonly sirOpenFailedMsg =
    'Unable to open the SIR document. Please try again later.';
  private static readonly permissionDeniedMsg =
    'You do not have permission to perform this action.';
  private static readonly sessionExpiredMsg =
    'Your session may have expired. Please sign in again.';
  private static readonly networkErrorMsg =
    'Unable to reach the server. Check your connection and try again.';

  private static readonly errorToastIcon = 'fa-light fa-circle-exclamation';
  private static readonly errorToastLifeMs = 5000;

  claimRow: ClaimRow;
  jobFunctions: JobFunction[] = [];
  jobTaskRefs: JobTaskRef[] = [];
  jobFunctionList: JobFunctionList;

  getJobTaskRefs(jobFunctionId: number): JobTaskRef[] {
    return this.jobTaskRefs.filter((f) => f.jobFunctionId === jobFunctionId);
  }

  async ngOnInit(): Promise<void> {
    this.claimRow = this.view.currentClaim();
    const claimKey: ClaimKey = {
      recordId: this.claimRow.recordId,
      recordSource: this.claimRow.recordSource,
    };
    try {
      this.jobFunctionList = await this.api.jobFunctions.get(claimKey);
      this.jobFunctions = this.jobFunctionList.jobFunctions;
      this.jobTaskRefs = this.jobFunctionList.jobTaskRefs;
    } catch (e: unknown) {
      this.jobFunctions = [];
      this.jobTaskRefs = [];
      const message =
        e instanceof HttpErrorResponse
          ? this.httpApiErrorMessage(e)
          : JobFunctionsComponent.loadFailedMsg;
      this.showErrorSnackbar(message);
    } finally {
      this.view.hideLoading();
    }
  }

  async openSir(dcn: string): Promise<void> {
    const trimmed = dcn?.trim();
    if (!trimmed) {
      return;
    }

    const dcnList: SirSSO = { dcn: [trimmed] } as SirSSO;

    try {
      const conduentSSO = await this.api.document.viewImage(
        this.claimRow.recordSource,
        this.claimRow.recordId,
        dcnList,
      );

      if (conduentSSO.samlResponse) {
        samlPost(
          conduentSSO.samlResponse,
          conduentSSO.url,
          'samlResponse',
        );
      } else {
        this.showErrorSnackbar(JobFunctionsComponent.sirSsoIncompleteMsg);
      }
    } catch (e: unknown) {
      const message =
        e instanceof HttpErrorResponse
          ? this.httpApiErrorMessage(e)
          : JobFunctionsComponent.sirOpenFailedMsg;
      this.showErrorSnackbar(message);
    }
  }

  private showErrorSnackbar(message: string): void {
    this.snackbar.show(
      message,
      'Error',
      JobFunctionsComponent.errorToastIcon,
      JobFunctionsComponent.errorToastLifeMs,
    );
  }

  private httpApiErrorMessage(e: HttpErrorResponse): string {
    if (e.status === 403) {
      return JobFunctionsComponent.permissionDeniedMsg;
    }
    if (e.status === 401) {
      return JobFunctionsComponent.sessionExpiredMsg;
    }
    if (e.status === 0) {
      return JobFunctionsComponent.networkErrorMsg;
    }

    const body = e.error;
    if (
      body &&
      typeof body === 'object' &&
      'message' in body &&
      typeof (body as { message?: unknown }).message === 'string'
    ) {
      const fromApi = (body as { message: string }).message.trim();
      if (fromApi.length > 0) {
        return this.sanitizeUserFacingErrorText(fromApi);
      }
    }
    return this.sanitizeUserFacingErrorText(
      e.message || `Request failed (${e.status})`,
    );
  }

  /** Hide infrastructure/crypto wording if API ever returns it unchanged. */
  private sanitizeUserFacingErrorText(text: string): string {
    const t = text.toLowerCase();
    if (
      t.includes('certificate') ||
      t.includes('localmachine') ||
      (t.includes('currentuser') && t.includes('store')) ||
      t.includes('x509store') ||
      t.includes('privatekeydistinguishedname')
    ) {
      return JobFunctionsComponent.sirOpenFailedMsg;
    }
    return text;
  }
}
